import { useQuery } from "@apollo/client/react";
import type { ErrorLike } from "@apollo/client";
import { useMemo } from "react";
import { ProductClass, ProductLevel, ServicesClass } from "../types/enums";
import { LocationLevel } from "../utils/locationUtils";
import { getNumericIdFromStringLocationId } from "../utils/locationUtils";
import { groups, stratify, InternMap } from "d3";
import { HierarchyNode } from "d3-hierarchy";

// Classification registry (single source of truth for query documents)
import { getClassificationQueryDocument } from "../classifications";

// Import GraphQL queries
import getLocationsMetadata from "../graphql/queries/getLocationsMetadata";
import getCountriesMetadata from "../graphql/queries/getCountriesMetadata";
import getProductYearMetadata from "../graphql/queries/getProductYearMetadata";
import getDeflatorsMetadata from "../graphql/queries/getDeflatorsMetadata";
import getCountryYearMetadata from "../graphql/queries/getCountryYearMetadata";
import getGroupYearMetadata from "../graphql/queries/getGroupYearMetadata";

// Domain types
import type {
  ProductMetadatum,
  ProductMetadataByLevel,
  DeflatorDatum,
  CountryDatum,
  LocationMetadatum,
  LocationYearDatum,
  ProductYearDatum,
  StratifyProductInput,
} from "../types/metadata";

import type { MetadataFetchState } from "../types/metadataState";

// Domain types are exported from ../types/metadata; not re-exported here to avoid barrel conflicts.

/** Shape returned when product-year data spans multiple years */
interface ProductYearGroup {
  groupYear: number;
  groupData: ProductYearDatum[];
}

/**
 * Convert Apollo query result fields into a MetadataFetchState discriminated union.
 */
function fromApolloResult<T>(
  data: T | undefined,
  loading: boolean,
  error: ErrorLike | undefined,
): MetadataFetchState<T> {
  if (error) return { status: "error", error: error instanceof Error ? error : new Error(String(error)) };
  if (loading) return { status: "loading" };
  if (data !== undefined) return { status: "success", data };
  return { status: "idle" };
}

// =============================================================================
// LOCATION HOOKS
// =============================================================================

/**
 * Hook to fetch all location metadata (countries, regions, subregions)
 */
export function useLocationsMetadata(): MetadataFetchState<{
  countries: LocationMetadatum[];
  regions: LocationMetadatum[];
  subregions: LocationMetadatum[];
}> {
  const { loading, error, data } = useQuery(
    getLocationsMetadata,
    {
      fetchPolicy: "cache-first",
    },
  );

  return useMemo(() => {
    const transformed = data
      ? {
          countries:
            data.countries?.filter(
              (c): c is NonNullable<typeof c> => c != null,
            ) ?? [],
          regions:
            data.regions?.filter(
              (r): r is NonNullable<typeof r> => r != null,
            ) ?? [],
          subregions:
            data.subregions?.filter(
              (s): s is NonNullable<typeof s> => s != null,
            ) ?? [],
        }
      : undefined;
    return fromApolloResult(transformed, loading, error);
  }, [data, loading, error]);
}

/**
 * Hook to fetch only countries metadata
 */
export function useCountriesMetadata(): MetadataFetchState<CountryDatum[]> {
  const { loading, error, data } = useQuery(
    getCountriesMetadata,
    {
      fetchPolicy: "cache-first",
    },
  );

  return useMemo(() => {
    const filtered =
      data?.countries?.filter(
        (c): c is NonNullable<typeof c> => c != null,
      ) ?? undefined;
    return fromApolloResult(filtered, loading, error);
  }, [data, loading, error]);
}

// =============================================================================
// PRODUCT HOOKS
// =============================================================================

/**
 * Hook to fetch product metadata for a specific classification.
 *
 * Uses the Classification Registry to select the correct GraphQL query
 * document, issuing a single `useQuery` call (no skip-flag fan-out).
 *
 * Accepts an optional servicesClass parameter (instead of reading from URL).
 */
export function useProductsMetadata(
  productClass: ProductClass,
  options?: { servicesClass?: string },
): MetadataFetchState<ProductMetadataByLevel> {
  const { servicesClass } = options ?? {};

  // Ensure productClass has a valid value (default to HS92 if undefined)
  const normalizedProductClass = productClass || ProductClass.HS92Products;

  // Look up the query document from the classification registry
  const queryDocument = getClassificationQueryDocument(normalizedProductClass);

  const { loading, error, data } = useQuery<ProductMetadataByLevel>(
    queryDocument,
    {
      variables: { servicesClass: servicesClass || undefined },
      fetchPolicy: "cache-first",
    },
  );

  return useMemo(() => fromApolloResult(data, loading, error), [data, loading, error]);
}

// =============================================================================
// DEFLATOR HOOKS
// =============================================================================

/**
 * Hook to fetch deflators metadata
 */
export function useDeflatorsMetadata(): MetadataFetchState<DeflatorDatum[]> {
  const { loading, error, data } = useQuery<{ deflators: DeflatorDatum[] }>(
    getDeflatorsMetadata,
    {
      fetchPolicy: "cache-first",
    },
  );

  return useMemo(
    () => fromApolloResult(data?.deflators, loading, error),
    [data, loading, error],
  );
}

// =============================================================================
// YEAR-BASED HOOKS
// =============================================================================

/**
 * Hook to fetch product-year metadata.
 * Accepts servicesClass as a parameter instead of reading from URL.
 */
export function useProductYearMetadata({
  yearRange,
  productClass,
  productLevel,
  servicesClass,
}: {
  yearRange: { yearMin: number; yearMax: number };
  productClass: ProductClass;
  productLevel: ProductLevel;
  servicesClass?: ServicesClass;
}): MetadataFetchState<ProductYearDatum[] | ProductYearGroup[]> {
  const { yearMin, yearMax } = yearRange;

  const { loading, error, data } = useQuery<{
    productYear: ProductYearDatum[];
  }>(getProductYearMetadata, {
    variables: {
      yearMin,
      yearMax,
      productClass,
      productLevel,
      servicesClass: servicesClass ?? ServicesClass.unilateral,
    },
    skip: !yearMin || !yearMax,
  });

  // Transform data to match expected format
  const transformedData = useMemo(() => {
    if (!data?.productYear) return undefined;

    if (yearMin === yearMax) {
      return data.productYear;
    }

    // Group by year for range queries
    const productYearGroupedByYear = groups(
      data.productYear,
      (d: ProductYearDatum) => d.year,
    ).map(
      (groupedProductYear: [number, ProductYearDatum[]]): ProductYearGroup => {
        const groupYear = groupedProductYear[0];
        const groupData = groupedProductYear[1];
        return { groupYear, groupData };
      },
    );

    return productYearGroupedByYear;
  }, [data, yearMin, yearMax]);

  return useMemo(
    () => fromApolloResult(transformedData, loading, error),
    [transformedData, loading, error],
  );
}

/**
 * Hook to fetch country-year metadata
 */
export function useCountryYearMetadata(
  countryId: string | undefined,
): MetadataFetchState<LocationYearDatum[]> {
  const numericCountryId = useMemo(
    () => getNumericIdFromStringLocationId(countryId),
    [countryId],
  );

  const { loading, error, data } = useQuery<{
    countryYear: LocationYearDatum[];
  }>(getCountryYearMetadata, {
    variables: { countryId: numericCountryId },
    skip: !numericCountryId,
  });

  return useMemo(
    () => fromApolloResult(data?.countryYear, loading, error),
    [data, loading, error],
  );
}

/**
 * Hook to fetch group-year metadata
 */
export function useGroupYearMetadata(
  groupId: string | undefined,
): MetadataFetchState<LocationYearDatum[]> {
  const numericGroupId = useMemo(
    () => getNumericIdFromStringLocationId(groupId),
    [groupId],
  );

  const { loading, error, data } = useQuery<{
    groupYear: LocationYearDatum[];
  }>(getGroupYearMetadata, {
    variables: { groupId: numericGroupId },
    skip: !numericGroupId,
  });

  return useMemo(
    () => fromApolloResult(data?.groupYear, loading, error),
    [data, loading, error],
  );
}

/**
 * Hook to fetch location-year metadata (country or group)
 */
export function useLocationYearMetadata({
  locationId,
  locationLevel,
}: {
  locationId: string | undefined;
  locationLevel: LocationLevel;
}): MetadataFetchState<LocationYearDatum[]> {
  const isCountry = locationLevel === LocationLevel.Country;
  const isGroup = locationLevel === LocationLevel.Group;

  const countryResult = useCountryYearMetadata(
    isCountry ? locationId : undefined,
  );
  const groupResult = useGroupYearMetadata(isGroup ? locationId : undefined);

  if (isCountry) {
    return countryResult;
  } else if (isGroup) {
    return groupResult;
  }

  return { status: "idle" };
}

export function usePreloadMetadata() {
  // Optionally preload locations if needed everywhere
}

// Import processing utilities (exported from utils, not re-exported here to avoid duplicates)
import {
  createProductLookupIndex,
  createYearLookupIndex,
  createProductHierarchyTree,
} from "../utils/metadataProcessing";

export function useProductLookupIndex(
  productClass: ProductClass,
  options?: { servicesClass?: string },
): MetadataFetchState<InternMap<string, ProductMetadatum>> {
  const productsState = useProductsMetadata(productClass, options);
  const data = productsState.status === "success" ? productsState.data : undefined;
  const loading = productsState.status === "loading";
  const error = productsState.status === "error" ? productsState.error : undefined;

  const lookup = useMemo(() => {
    if (!data) return undefined;

    const allProducts: ProductMetadatum[] = [
      ...data.section,
      ...data.twoDigit,
      ...data.fourDigit,
      ...data.sixDigit,
    ];
    return createProductLookupIndex(allProducts);
  }, [data]);

  return useMemo(
    () => fromApolloResult(lookup, loading, error),
    [lookup, loading, error],
  );
}

export function useDeflatorsLookupIndex(): MetadataFetchState<
  InternMap<number, DeflatorDatum>
> {
  const deflatorsState = useDeflatorsMetadata();
  const data = deflatorsState.status === "success" ? deflatorsState.data : undefined;
  const loading = deflatorsState.status === "loading";
  const error = deflatorsState.status === "error" ? deflatorsState.error : undefined;

  const lookup = useMemo(() => {
    if (!data) return undefined;
    return createYearLookupIndex(data);
  }, [data]);

  return useMemo(
    () => fromApolloResult(lookup, loading, error),
    [lookup, loading, error],
  );
}

export function useProductHierarchyTree(
  productClass: ProductClass,
  options?: { servicesClass?: string },
): MetadataFetchState<HierarchyNode<StratifyProductInput>> {
  const productsState = useProductsMetadata(productClass, options);
  const data = productsState.status === "success" ? productsState.data : undefined;
  const loading = productsState.status === "loading";
  const error = productsState.status === "error" ? productsState.error : undefined;

  const tree = useMemo(() => {
    if (!data) {
      return stratify<StratifyProductInput>()
        .id((d) => d.productId)
        .parentId(() => null)([{ productId: "root", parent: null }]);
    }

    const allProducts: ProductMetadatum[] = [
      ...data.section,
      ...data.twoDigit,
      ...data.fourDigit,
      ...data.sixDigit,
    ];
    return createProductHierarchyTree(allProducts);
  }, [data]);

  return useMemo(
    () => fromApolloResult(tree, loading, error),
    [tree, loading, error],
  );
}
